import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { MultiSelectModule } from 'primeng/multiselect';
import { CalendarModule } from 'primeng/calendar';
import {
  HygieneInspectionStatus,
  HygieneInspectionStatusLabels,
} from '../../../models/status/hygiene-inspection-status.enum';
import { OrderService } from '../../../../core/order.service';
import { HttpParams } from '@angular/common/http';
import { QueryParamsService } from '../../../../core/query-params.service';
import { ActivatedRoute } from '@angular/router';
import { LoggerService } from '../../../../core/logger.service';
import { OrderStatus } from '../../../models/status/order-status.enum';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../../button/button.component';
import {
  EnergeticInspectionStatus,
  EnergeticInspectionStatusLabels,
} from '../../../models/status/energetic-inspection-status.enum';

@Component({
  selector: 'app-filter',
  standalone: true,
  imports: [
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    DropdownModule,
    FormsModule,
    MultiSelectModule,
    CalendarModule,
    ButtonComponent,
    CommonModule,
  ],
  templateUrl: './filter.component.html',
  styleUrl: './filter.component.scss',
})
export class FilterComponent implements OnInit {
  @Output() filterListEvent = new EventEmitter<HttpParams>();
  auftragsnummer = '';
  standort = '';
  auftraggeber = '';
  showClearButton = false;

  rangeDatesStart: Date[] | undefined;
  rangeDatesEnd: Date[] | undefined;

  inspectorFilterItems: string[] | undefined;
  selectedInspector: string | undefined;

  selectedStatus: string[] = [];

  orderStatusItems = [
    {
      label: 'Angelegt',
      value: this.toSelectedStatusValue('status', OrderStatus.Created),
    },
    {
      label: 'Geplant',
      value: this.toSelectedStatusValue('status', OrderStatus.Planned),
    },
    {
      label: 'In Durchführung',
      value: this.toSelectedStatusValue(
        'status',
        OrderStatus.InProcessByInspector
      ),
    },
    {
      label: 'Vom Prüfer Abgeschlossen',
      value: this.toSelectedStatusValue(
        'status',
        OrderStatus.FinishedByInspector
      ),
    },
    {
      label: 'Versandbereit',
      value: this.toSelectedStatusValue('status', OrderStatus.ReadyForShipment),
    },
    {
      label: 'Versendet',
      value: this.toSelectedStatusValue(
        'status',
        OrderStatus.ShippedToCustomer
      ),
    },
  ];

  hygieneStatusItems = [
    {
      label: HygieneInspectionStatusLabels.PENDING,
      value: this.toSelectedStatusValue(
        'hygieneStatus',
        HygieneInspectionStatus.Pending
      ),
    },
    {
      label: HygieneInspectionStatusLabels.IN_PROGRESS,
      value: this.toSelectedStatusValue(
        'hygieneStatus',
        HygieneInspectionStatus.InProgress
      ),
    },
    {
      label: HygieneInspectionStatusLabels.WAITING_FOR_LAB,
      value: this.toSelectedStatusValue(
        'hygieneStatus',
        HygieneInspectionStatus.WaitingForLab
      ),
    },
    {
      label: HygieneInspectionStatusLabels.IN_REVIEW,
      value: this.toSelectedStatusValue(
        'hygieneStatus',
        HygieneInspectionStatus.InReview
      ),
    },
    {
      label: HygieneInspectionStatusLabels.COMPLETED,
      value: this.toSelectedStatusValue(
        'hygieneStatus',
        HygieneInspectionStatus.Completed
      ),
    },
  ];

  energeticStatusItems = [
    {
      label: EnergeticInspectionStatusLabels.PENDING,
      value: this.toSelectedStatusValue(
        'energeticStatus',
        EnergeticInspectionStatus.Pending
      ),
    },
    {
      label: EnergeticInspectionStatusLabels.IN_PROGRESS,
      value: this.toSelectedStatusValue(
        'energeticStatus',
        EnergeticInspectionStatus.InProgress
      ),
    },
    {
      label: EnergeticInspectionStatusLabels.WAITING_FOR_DATA,
      value: this.toSelectedStatusValue(
        'energeticStatus',
        EnergeticInspectionStatus.WaitingForData
      ),
    },
    {
      label: EnergeticInspectionStatusLabels.IN_REVIEW,
      value: this.toSelectedStatusValue(
        'energeticStatus',
        EnergeticInspectionStatus.InReview
      ),
    },
    {
      label: EnergeticInspectionStatusLabels.COMPLETED,
      value: this.toSelectedStatusValue(
        'energeticStatus',
        EnergeticInspectionStatus.Completed
      ),
    },
  ];

  statusFilterItems = [
    {
      label: 'Auftragsstatus',
      value: 'order_status',
      items: this.orderStatusItems,
    },
    {
      label: 'Hygieneinspektionsstatus',
      value: 'hygiene_status',
      items: this.hygieneStatusItems,
    },
    {
      label: 'Energetische Inspektionsstatus',
      value: 'energetic_status',
      items: this.energeticStatusItems,
    },
  ];

  constructor(
    private orderService: OrderService,
    private queryService: QueryParamsService,
    private route: ActivatedRoute,
    private loggerService: LoggerService
  ) {}

  ngOnInit(): void {
    this.getAllInspectors();
    this.loadFilterFromQueryParams();
    this.updateShowClearButton();
  }

  filterOrders() {
    const filterParams: Record<string, any> = this.createFilterObject();
    filterParams['page'] = 0;
    const httpParams = this.queryService.createHttpParams(filterParams);
    this.filterListEvent.emit(httpParams);
  }

  clearFilter() {
    this.auftragsnummer = '';
    this.auftraggeber = '';
    this.standort = '';
    this.selectedInspector = undefined;
    this.rangeDatesStart = undefined;
    this.rangeDatesEnd = undefined;
    this.selectedStatus = [];

    const httpParams = this.queryService.getCurrentQueryParams();
    const filterParams: Record<string, any> = {
      page: 0,
      size: httpParams.get('size'),
    };

    const filter: HttpParams =
      this.queryService.convertQueryParamsToHttpParams(filterParams);

    this.filterListEvent.emit(filter);
    this.showClearButton = false;
  }

  onInspectorChange() {
    this.updateShowClearButton();
  }

  isStartDateRangeValid(): boolean {
    const startDates = this.rangeDatesStart;
    if (!startDates || startDates.length === 0) return true;
    const [start, end] = startDates;

    return !!start && !!end;
  }

  isEndDateRangeValid(): boolean {
    const endDates = this.rangeDatesEnd;
    if (!endDates || endDates.length === 0) return true;
    const [start, end] = endDates;

    return !!start && !!end;
  }

  private createFilterObject(): Record<string, any> {
    const filterParams: Record<string, any> = {};
    const httpParams = this.queryService.getCurrentQueryParams();

    const page = httpParams.get('page');
    const size = httpParams.get('size');

    if (page) {
      filterParams['page'] = page;
    }

    if (size) {
      filterParams['size'] = size;
    }

    // Only add parameter if not empty
    if (this.auftragsnummer) {
      filterParams['orderNumber'] = this.auftragsnummer;
    }

    if (this.auftraggeber) {
      filterParams['customer'] = this.auftraggeber;
    }

    if (this.standort) {
      filterParams['location'] = this.standort;
    }

    if (this.selectedInspector) {
      filterParams['inspectorFullName'] = this.selectedInspector;
    }

    if (this.rangeDatesStart?.[0] && this.rangeDatesStart?.[1]) {
      filterParams['startDateRangeStart'] = this.getFormattedDate(
        this.rangeDatesStart[0]
      );
      filterParams['startDateRangeEnd'] = this.getFormattedDate(
        this.rangeDatesStart[1]
      );
    }

    if (this.rangeDatesEnd?.[0] && this.rangeDatesEnd?.[1]) {
      filterParams['endDateRangeStart'] = this.getFormattedDate(
        this.rangeDatesEnd[0]
      );
      filterParams['endDateRangeEnd'] = this.getFormattedDate(
        this.rangeDatesEnd[1]
      );
    }

    const orderStatus = this.extractSelectedStatuses<OrderStatus>('status');

    const hygieneStatus =
      this.extractSelectedStatuses<HygieneInspectionStatus>('hygieneStatus');

    const energeticStatus =
      this.extractSelectedStatuses<EnergeticInspectionStatus>('energeticStatus');

    if (orderStatus.length > 0) {
      filterParams['status'] = orderStatus;
    }

    if (hygieneStatus.length > 0) {
      filterParams['hygieneStatus'] = hygieneStatus;
    }

    if (energeticStatus.length > 0) {
      filterParams['energeticStatus'] = energeticStatus;
    }
    return filterParams;
  }

  private loadFilterFromQueryParams(): void {
    this.route.queryParams.subscribe((params) => {
      const httpParams =
        this.queryService.convertQueryParamsToHttpParams(params);

      this.auftragsnummer = httpParams.get('orderNumber') || '';
      this.auftraggeber = httpParams.get('customer') || '';
      this.standort = httpParams.get('location') || '';
      this.selectedInspector = httpParams.get('inspectorFullName') || '';

      const statusesArray = httpParams.getAll('status') || [];
      const hygieneStatusesArray = httpParams.getAll('hygieneStatus') || [];
      const energeticStatusesArray = httpParams.getAll('energeticStatus') || [];

      // Processing different arrays
      const statuses = statusesArray.flatMap((statusString) =>
        statusString.split(',').map((value) => value.trim())
      );
      const hygieneStatuses = hygieneStatusesArray.flatMap(
        (hygieneStatusString) =>
          hygieneStatusString.split(',').map((value) => value.trim())
      );

      const energeticStatuses = energeticStatusesArray.flatMap(
        (energeticStatusString) =>
          energeticStatusString.split(',').map((value) => value.trim())
      );

      this.selectedStatus = [
        ...statuses.map((status) => this.toSelectedStatusValue('status', status)),
        ...hygieneStatuses.map((status) =>
          this.toSelectedStatusValue('hygieneStatus', status)
        ),
        ...energeticStatuses.map((status) =>
          this.toSelectedStatusValue('energeticStatus', status)
        ),
      ];

      // Dates Handling
      const startDateRangeStart = httpParams.get('startDateRangeStart') || '';
      const startDateRangeEnd = httpParams.get('startDateRangeEnd') || '';

      const endDateRangeStart = httpParams.get('endDateRangeStart') || '';
      const endDateRangeEnd = httpParams.get('endDateRangeEnd') || '';

      if (startDateRangeStart && startDateRangeEnd) {
        this.rangeDatesStart = [
          new Date(startDateRangeStart),
          new Date(startDateRangeEnd),
        ];
      } else {
        this.rangeDatesStart = undefined;
      }

      if (endDateRangeStart && endDateRangeEnd) {
        this.rangeDatesEnd = [
          new Date(endDateRangeStart),
          new Date(endDateRangeEnd),
        ];
      } else {
        this.rangeDatesEnd = undefined;
      }
    });
  }

  private updateShowClearButton() {
    this.showClearButton = !!this.selectedInspector;
  }

  private toSelectedStatusValue(
    key: 'status' | 'hygieneStatus' | 'energeticStatus',
    value: string
  ): string {
    return `${key}:${value}`;
  }

  private extractSelectedStatuses<T extends string>(
    key: 'status' | 'hygieneStatus' | 'energeticStatus'
  ): T[] {
    const prefix = `${key}:`;

    return this.selectedStatus
      .filter((status) => status.startsWith(prefix))
      .map((status) => status.slice(prefix.length) as T);
  }

  private getAllInspectors() {
    this.orderService.getAllInspectorNames().subscribe({
      next: (inspectorNames) => {
        this.inspectorFilterItems = inspectorNames;
      },
      error: () => {
        this.loggerService.error('Failed to fetch inspectors');
      },
    });
  }

  private getFormattedDate(date?: Date): string {
    return date ? this.orderService.formatDate(date) : '';
  }
}
